import { useCallback, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { getStorageUrl, needsStorageUrl } from '../../helpers/global';

const STORAGE_URL_STALE_MS = 5 * 60 * 1000; // 5 min – signed URLs stay valid

type FileLike = { url: string };

/**
 * Resolves Firebase/Google Cloud Storage URLs that require a token for the given
 * files. Returns a getter for display URLs and loading state. Reuses React
 * Query cache (same key as useStorageUrl) so repeated URLs aren't refetched.
 */
export default function useResolvedFileUrls<T extends FileLike>(
  files: T[] | undefined,
) {
  const urlsToResolve = useMemo(
    () =>
      files
        ? [...new Set(files.map(f => f.url).filter(Boolean))].filter(
            (url): url is string => needsStorageUrl(url),
          )
        : [],
    [files],
  );

  const storageQueries = useQueries({
    queries: urlsToResolve.map(url => ({
      queryKey: ['storageUrl', url],
      queryFn: () => getStorageUrl(url),
      staleTime: STORAGE_URL_STALE_MS,
      enabled: Boolean(url),
    })),
  });

  const queryDataByIndex = storageQueries.map(q => q.data);
  const resolvedUrls = useMemo(() => {
    const map: Record<string, string> = {};
    urlsToResolve.forEach((url, i) => {
      map[url] = queryDataByIndex[i] ?? url;
    });
    return map;
  }, [urlsToResolve, queryDataByIndex]);

  const getDisplayUrl = useCallback(
    (url: string) => resolvedUrls[url] ?? url,
    [resolvedUrls],
  );

  const isLoading = storageQueries.some(q => q.isLoading);

  return { getDisplayUrl, resolvedUrls, isLoading };
}
